import { reverse } from "@reactivated";

import {
    PhoneVerificationChannel,
    PhoneVerificationSendResponse,
    PhoneVerificationSendStatus,
    PhoneVerificationVerifyResponse,
} from "../models/phoneVerification";
import { check } from "../models/utils";
import { CSRF_HEADER, fetchJson, getCSRFToken } from "../utils/ajax";

/**
 * Send a phone-ownership verification code (by SMS or voice) to the given
 * number, recorded against the current user account.
 *
 * Throws HttpError with status 429 when the resend cooldown or send rate
 * limits are hit, or 400 if the channel isn't available / the number is
 * invalid.
 */
export const sendPhoneVerificationCode = async (
    phone: string,
    channel: PhoneVerificationChannel,
): Promise<PhoneVerificationSendStatus> => {
    const body = await fetchJson(reverse("phoneverification:send"), {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            [CSRF_HEADER]: await getCSRFToken(),
        },
        body: JSON.stringify({ phone: phone, channel: channel }),
    });
    return check(PhoneVerificationSendResponse.decode(body)).status;
};

/**
 * Verify a code previously sent by sendPhoneVerificationCode.
 *
 * Throws HttpError with status 400 when the code is wrong, expired, or the
 * attempt limit has been exhausted.
 */
export const verifyPhoneCode = async (
    phone: string,
    code: string,
): Promise<PhoneVerificationVerifyResponse> => {
    const body = await fetchJson(reverse("phoneverification:verify"), {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            [CSRF_HEADER]: await getCSRFToken(),
        },
        body: JSON.stringify({ phone: phone, code: code }),
    });
    return check(PhoneVerificationVerifyResponse.decode(body));
};
